// Channel Mixer — Photoshop-style adjustment.
//
// Each output channel is a weighted combination of the input channels:
//
//   R_out = r * rr/100  + g * rg/100  + b * rb/100  + cR
//   G_out = r * gr/100  + g * gg/100  + b * gb/100  + cG
//   B_out = r * br/100  + g * bg/100  + b * bb/100  + cB
//
// Monochrome mode computes a single gray value using the Red mixer settings
// and writes it to all three output channels:
//
//   gray  = r * rr/100  + g * rg/100  + b * rb/100  + cR
//   R = G = B = clamp(gray, 0, 255)
//
// Weights range from -200 % to +200 %. Constants range from -200 to +200.
// Alpha is never touched.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface ChannelMixerParams {
  red: { red: number; green: number; blue: number; constant: number };
  green: { red: number; green: number; blue: number; constant: number };
  blue: { red: number; green: number; blue: number; constant: number };
  monochrome: boolean;
}

const DEFAULT_PARAMS: ChannelMixerParams = {
  red: { red: 100, green: 0, blue: 0, constant: 0 },
  green: { red: 0, green: 100, blue: 0, constant: 0 },
  blue: { red: 0, green: 0, blue: 100, constant: 0 },
  monochrome: false,
};

export const ChannelMixer: Filter<ChannelMixerParams> = {
  id: "channel-mixer",
  displayName: "Channel Mixer",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: ChannelMixerParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const { red: rm, green: gm, blue: bm, monochrome } = params;

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o] ?? 0;
      const g = src[o + 1] ?? 0;
      const b = src[o + 2] ?? 0;
      const a = src[o + 3] ?? 0;

      if (a === 0) {
        dst[o] = 0;
        dst[o + 1] = 0;
        dst[o + 2] = 0;
        dst[o + 3] = 0;
        continue;
      }

      if (monochrome) {
        const gray = clampByte(
          r * (rm.red / 100) +
          g * (rm.green / 100) +
          b * (rm.blue / 100) +
          rm.constant,
        );
        dst[o] = gray;
        dst[o + 1] = gray;
        dst[o + 2] = gray;
      } else {
        dst[o] = clampByte(
          r * (rm.red / 100) +
          g * (rm.green / 100) +
          b * (rm.blue / 100) +
          rm.constant,
        );
        dst[o + 1] = clampByte(
          r * (gm.red / 100) +
          g * (gm.green / 100) +
          b * (gm.blue / 100) +
          gm.constant,
        );
        dst[o + 2] = clampByte(
          r * (bm.red / 100) +
          g * (bm.green / 100) +
          b * (bm.blue / 100) +
          bm.constant,
        );
      }

      dst[o + 3] = a;
    }

    return out;
  },
};

function clampByte(v: number): number {
  const rounded = Math.round(v);
  return rounded < 0 ? 0 : rounded > 255 ? 255 : rounded;
}
